import roles from "helpers/roles";
import { encryptToken } from "./authenticationCrypto";

// Keep in sync with keys written in setStorage, setMeStorage, and api.js refresh logic
const AUTH_KEYS = [
	"me",
	"token",
	"originalLogin",
	"loginType",
	"clientAdminMode",
	"clientUserId",
	"settingType",
	// Locale state is per-user/per-session: drop it on logout so the login page
	// is English and the next sign-in re-resolves the effective locale cleanly.
	"appLocale",
	"localeResolvedSiteApp",
];

export function clearAuthStorage() {
	AUTH_KEYS.forEach((key) => {
		sessionStorage.removeItem(key);
		localStorage.removeItem(key);
	});
}

export function setMeStorage(data) {
	return new Promise((res) => {
		const modifiedData = {
			...data,
			jwtToken: encryptToken(data.jwtToken),
			refreshToken: encryptToken(data.refreshToken),
		};
		localStorage.setItem("me", JSON.stringify(modifiedData));
		sessionStorage.setItem("me", JSON.stringify(modifiedData));
		res(data);
	});
}

export function setStorage(res) {
	return new Promise(async (resolve) => {
		localStorage.setItem("token", encryptToken(res.jwtToken));
		sessionStorage.setItem("token", encryptToken(res.jwtToken));
		let response = res;
		if (response.position !== null) {
			response["role"] = roles.siteUser;
		}
		await setMeStorage(response);

		resolve(response);
	});
}

// Context-scoped keys that only make sense inside a site app / client-admin
// session. Dropped when the user returns to the application portal.
const SITE_CONTEXT_KEYS = ["clientAdminMode", "userCrumbs", "settingType"];

function readJson(key) {
	try {
		return (
			JSON.parse(sessionStorage.getItem(key)) ||
			JSON.parse(localStorage.getItem(key))
		);
	} catch {
		return null;
	}
}

/**
 * Restore the global (portal-level) identity after the user has entered a
 * site app. LoginToSiteApp overwrites `me` with a site-scoped payload
 * (siteAppID, position, siteName…) and there is no server endpoint to issue a
 * global token again, so we rebuild `me` from the `originalLogin` snapshot
 * taken at login while keeping the *current* tokens — those are the ones the
 * server still accepts (the originals may have been rotated by a refresh).
 *
 * Returns the restored `me` object, or null when no snapshot exists.
 */
export function restoreGlobalLoginStorage() {
	const originalLogin = readJson("originalLogin");
	if (!originalLogin) return null;

	const currentMe = readJson("me");
	const restored = {
		...originalLogin,
		jwtToken: currentMe?.jwtToken ?? originalLogin.jwtToken,
		refreshToken: currentMe?.refreshToken ?? originalLogin.refreshToken,
	};

	const serialised = JSON.stringify(restored);
	localStorage.setItem("me", serialised);
	sessionStorage.setItem("me", serialised);
	SITE_CONTEXT_KEYS.forEach((key) => {
		localStorage.removeItem(key);
		sessionStorage.removeItem(key);
	});

	return restored;
}
